import React from 'react';
import I18n from 'i18n-js';
import { RCAlertBox, RCButton, RCStarRatingSelector, RCTextArea, RCTextInput } from '@reverbdotcom/cadence/components';
// TODO update this to import from commmons
// import { gql } from '@reverbdotcom/commons/src/gql';
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import {
  Core_ProductReview_UpdateMutation,
  Core_ProductReview_UpdateMutationVariables,
  ProductReviewCSPFieldsFragment,
} from '@reverbdotcom/commons/src/gql/graphql';
import { ErrorAlertBox } from '@reverbdotcom/commons/src/ErrorAlertBox';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { ProductReviewGuidelines } from './ProductReviewGuidelines';
import { ProductReviewWarning } from './ProductReviewWarning';

interface Props {
  cspId: string;
  myReview: ProductReviewCSPFieldsFragment['myReview'];
  trackingDisplayStyle: 'product' | 'csp';
  showHeader?: boolean;
}

export function ProductReviewForm({
  cspId,
  myReview,
  trackingDisplayStyle,
  showHeader = false,
}: Props) {
  const [rating, setRating] = React.useState(myReview?.rating || 0);
  const [title, setTitle] = React.useState(myReview?.title || '');
  const [body, setBody] = React.useState(myReview?.body || '');
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);
  const [mutate, { errors }] = useMutation<
    Core_ProductReview_UpdateMutation,
    Core_ProductReview_UpdateMutationVariables
  >(
    UPDATE_PRODUCT_REVIEW_MUTATION,
  );

  async function submit(e: React.ChangeEvent<HTMLFormElement>) {
    e.preventDefault();

    const { data } = await mutate({
      variables: {
        input: {
          comparisonShoppingPageId: cspId,
          rating,
          body,
          title,
        },
      },
    });

    if (data.updateProductReview) {
      setShowSuccessMessage(true);

      trackEvent({
        eventName: MParticleEventName.ProductReviewAction,
        productReviewInteraction: ProductReviewInteraction.SubmitReview,
        displayStyle: trackingDisplayStyle,
        cspId: cspId,
      });
    }
  }

  if (showSuccessMessage) {
    return (
      <div className="mb-8">
        <RCAlertBox type="success">
          {I18n.t('discovery.productReviews.header.successMessage')}
        </RCAlertBox>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mb-8">
      {errors.length > 0 &&
        <div className="mb-2">
          <ErrorAlertBox errors={errors} />
        </div>
      }
      {showHeader &&
        <h4 className="weight-bold mb-2">
          {I18n.t('discovery.productReviews.form.createReview')}
        </h4>
      }
      <RCStarRatingSelector
        onSelect={(value) => setRating(value)}
        rating={rating}
        inputName="csp-review-rating"
        inputId="csp-review-rating"
        label={I18n.t('discovery.productReviews.form.rating')}
      />
      <RCTextInput
        id="title"
        name="title"
        label={I18n.t('discovery.productReviews.form.title')}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <RCTextArea
        id="review"
        name="review"
        label={I18n.t('discovery.productReviews.form.review')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        required
      />
      <div className="mb-4">
        <RCButton
          isSubmit
          variant="filled"
        >
          {I18n.t('discovery.productReviews.form.submit')}
        </RCButton>
      </div>
      <ProductReviewWarning review={body} />
      <ProductReviewGuidelines />
    </form>
  );
}

const UPDATE_PRODUCT_REVIEW_MUTATION = gql`
  mutation Core_ProductReview_Update(
    $input: Input_core_apimessages_UpdateProductReviewRequest
  ) {
    updateProductReview(input: $input) {
      id
    }
  }
`;
